/* eslint-disable max-lines */
import { FC, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  IOperatorInvoice,
  IOperatorInvoicePreview
} from "../../interfaces/endpoints/IOperatorInvoice";
import { OperatorsSelectList } from "../../interfaces/endpoints/IOperators";
import {
  generateOperatorInvoice,
  getOperatorInvoicePreview
} from "../../services/OperatorInvoice.service";
import Box from "../ui/Box";
import Button from "../ui/Button";
import { RangePickerHandle } from "../ui/datepicker/Datepicker.types";
import RangePicker from "../ui/datepicker/range/RangePicker";
import Select from "../ui/select/Select";
import PreviewSummary from "./PreviewSummary";

interface GenerateInvoiceFormProps {
  onGenerated?: (invoice: IOperatorInvoice) => void;
  operators: OperatorsSelectList[];
}

const toIsoFromLocal = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}T00:00:00Z`;
};

const resolveError = (err: unknown, fallback: string) => {
  const e = err as {
    response?: { status?: number; data?: unknown };
  };
  if (typeof e?.response?.data === "string" && e.response.data) {
    return e.response.data;
  }
  if (e?.response?.status === 401 || e?.response?.status === 403) {
    return "Ju nuk keni qasje ne kete veprim.";
  }
  return fallback;
};

const GenerateInvoiceForm: FC<GenerateInvoiceFormProps> = ({
  onGenerated,
  operators
}) => {
  const [selectedOperators, setSelectedOperators] = useState<
    OperatorsSelectList[]
  >([]);
  const [fromDate, setFromDate] = useState<Date | undefined>();
  const [toDate, setToDate] = useState<Date | undefined>();
  const [preview, setPreview] = useState<IOperatorInvoicePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const periodPickerRef = useRef<RangePickerHandle>(null);

  // At least one operator must be selected; period is shared across all of them.
  const canRun = selectedOperators.length > 0 && !!fromDate && !!toDate;

  const handlePreview = async () => {
    if (!canRun || !fromDate || !toDate) return;
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await getOperatorInvoicePreview({
        operatorIds: selectedOperators.map((o) => o.id),
        from: toIsoFromLocal(fromDate),
        to: toIsoFromLocal(toDate)
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(resolveError(err, "Ndodhi nje gabim gjate preview."), {
        position: "top-right"
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleGenerate = async () => {
    if (!canRun || !fromDate || !toDate) return;
    setGenerating(true);
    try {
      const res = await generateOperatorInvoice({
        operatorIds: selectedOperators.map((o) => o.id),
        from: toIsoFromLocal(fromDate),
        to: toIsoFromLocal(toDate)
      });
      toast.success("Fatura u gjenerua.", { position: "top-right" });
      setPreview(null);
      onGenerated?.(res.data);
    } catch (err) {
      toast.error(resolveError(err, "Ndodhi nje gabim gjate gjenerimit."), {
        position: "top-right"
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <Box className="mb-4">
        <h2 className="text-lg font-semibold mb-4">Gjenero fature te re</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <Select
            key={`operators-${operators.length}`}
            options={operators}
            searchFunc={(o, search) =>
              !search ||
              (o.operatorName ?? "")
                .toLocaleLowerCase()
                .startsWith(search.toLocaleLowerCase())
            }
            selectType="multiple"
            enableKeyboardNav
            // Enter on a highlighted operator closes the search and pops the
            // period calendar open + focused, so the flow is type → arrow →
            // Enter → pick dates with no extra click. Tradeoff: Enter no longer
            // multi-picks in one pass here — reopen the picker to add more.
            onEnterSelect={() => periodPickerRef.current?.open()}
            onSelectedListChange={(selected) => setSelectedOperators(selected)}
            selectedItems={selectedOperators}
            getOptionValue={(o) => o?.id}
            label="Operatoret"
            placeholder="Kerko operatoret..."
            getOptionLabel={(o) => o.operatorName || `#${o.id}`}
            itemToString={(o) => o?.operatorName || ""}
          />
          <div>
            <label className="text-neutral block mb-1.5 text-sm leading-[17px]">
              Periudha
            </label>
            <RangePicker
              ref={periodPickerRef}
              className="flex flex-row items-center w-full"
              range={
                fromDate && toDate ? { from: fromDate, to: toDate } : undefined
              }
              pickerProps={{ toDate: new Date() }}
              hasHelpers
              onApply={(r) => {
                setFromDate(r?.from);
                setToDate(r?.to);
              }}
            />
          </div>
          <Button
            btnType="custom"
            className="border border-primary text-primary h-10"
            onClick={handlePreview}
            disabled={!canRun || loadingPreview || generating}
          >
            {loadingPreview ? "Duke ngarkuar..." : "Preview"}
          </Button>
          <Button
            className="h-10"
            onClick={handleGenerate}
            disabled={!canRun || generating || loadingPreview}
          >
            {generating ? "Duke gjeneruar..." : "Gjenero"}
          </Button>
        </div>
      </Box>

      {preview && <PreviewSummary preview={preview} />}
    </div>
  );
};

export default GenerateInvoiceForm;
